import { Company } from '@shared-ikp/model/company.model';
import { UmmeldungMetadata } from '../../../../generated/model/ummeldung-metadata.model';
import { Ummeldung } from '../../../../generated/model/ummeldung.model';
import { MeterPoint } from '../../../model/meter-point.model';
import { UmmeldungStore } from '../ummeldung.store';

export type UmmeldungStoreType = InstanceType<typeof UmmeldungStore>;

/**
 * All the wizard is handed when it is opened, carried in the navigation state. Identifiers only, on
 * purpose: `ummeldungWizardStartResolver` loads the actual data before the route activates, so a
 * payload here would only be a second, staler copy — the navigation state is restored by the browser
 * on reload and would then refill the wizard from a snapshot taken at click time.
 */
/**
 * How the wizard was opened. `upload` skips the form entirely and goes to the Formular-hochladen
 * step, for an Ummeldung that was printed, signed and scanned outside the portal.
 */
export type UmmeldungWizardStartMode = 'edit' | 'upload';

export interface UmmeldungWizardStartData {
  vertragsnummer: string;
  zaehlpunktId: string;
  /** Set when an already saved Ummeldung is resumed; absent when a new one is started. */
  editableUmmeldungId?: string;
  /** Defaults to `edit` when absent, so an older navigation state still opens the normal wizard. */
  mode?: UmmeldungWizardStartMode;
}

export interface UmmeldungWizardNavigationState {
  ummeldungStart?: UmmeldungWizardStartData;
}

export function getUmmeldungWizardStartDataFromState(state: unknown): UmmeldungWizardStartData | null {
  if (typeof state !== 'object' || state === null) {
    return null;
  }

  const startData = (state as UmmeldungWizardNavigationState).ummeldungStart;
  if (!startData?.vertragsnummer || !startData.zaehlpunktId) {
    return null;
  }

  return startData;
}

/**
 * The complete state a wizard session runs on, assembled by the start resolver and written into the
 * store in a single `initialize()`. Everything here is loaded — the store never fetches, and no step
 * can observe it half-populated.
 */
export interface UmmeldungWizardData {
  activeUmmeldung: Ummeldung;
  metadata: UmmeldungMetadata;
  aktuellerZaehlpunkt: MeterPoint;
  zaehlpunktUnternehmen: Company | null;
  accountUnternehmen: Company[];
  verwalteteUnternehmen: Company[];
  /** Drives the `omitted` / `skipIfFulfilled` conditions on the steps — see ummeldung.wizard.ts. */
  uploadMode: boolean;
}
